import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CommentText,
  CommentMetadata,
  CommentGroup,
  CommentContent,
  CommentAvatar,
  CommentActions,
  CommentAction,
  CommentAuthor,
  Comment,
  Button,
  Icon,
  Form,
  FormTextArea,
} from 'semantic-ui-react';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import AjaxController from '../system/ajaxController';
import './contract.css';

const Max2ContractComments = () => {
  // local State
  const [comments, setComments]               = useState([]);
  const [showAddComment, setShowAddComment]   = useState(false);
  const [newComment, setNewComment]           = useState('');
  const [editingComment, setEditingComment]   = useState(null);
  const [editedComment, setEditedComment]     = useState('');
  const [replyingTo, setReplyingTo]           = useState(null); // Track the comment being replied to

  // global state
  const backendUrl  = useSelector((state) => state.app.backend.baseProtocol + '://' + state.app.backend.url);
  const contractId  = useSelector((state) => state.app.contractId);
  const userDetails = useSelector((state) => state.app.user);

  const { t } = useTranslation();

  // Snackbar
  const { enqueueSnackbar } = useSnackbar();

  // Memoized AjaxController instance
  const api = useMemo(() => new AjaxController(backendUrl), [backendUrl]);

  // Fetch comments on component mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const data = await api.get(`/contract/${contractId}/comments`);
        setComments(data);
      } catch (error) {
        enqueueSnackbar(t('contract.comments.error.load'), { variant: 'error' });
      }
    };

    fetchComments();
  }, [contractId, api, enqueueSnackbar, t]);

  // Add a new comment or reply
  const handleAddComment = async () => {
    try {
      const endpoint = replyingTo 
        ? `/contract/${contractId}/comment/${replyingTo}/reply` 
        : `/contract/${contractId}/comment`;
      const data = await api.post(endpoint, { text: newComment, author: userDetails.name });
      setComments(data.comments);
      setNewComment('');
      setShowAddComment(false);
      setReplyingTo(null);
      enqueueSnackbar(t('contract.comments.success.add'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('contract.comments.error.add'), { variant: 'error' });
    }
  };

  // Edit an existing comment
  const handleEditComment = async (commentId) => {
    try {
      const data = await api.put(`/contract/${contractId}/comment/${commentId}`, { text: editedComment });
      setComments(data.comments);
      setEditingComment(null);
      setEditedComment('');
      enqueueSnackbar(t('contract.comments.success.edit'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('contract.comments.error.edit'), { variant: 'error' });
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/contract/${contractId}/comment/${commentId}`);
      setComments(comments.filter((comment) => comment.id !== commentId));
      enqueueSnackbar(t('contract.comments.success.delete'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('contract.comments.error.delete'), { variant: 'error' });
    }
  };

  return (
    <>
      <CommentGroup>
        {comments.map((comment) => (
          <Comment key={comment.id}>
            <CommentAvatar src="./resources/icons/user.png" />
            <CommentContent>
              <CommentAuthor as="a">{comment.author}</CommentAuthor>
              <CommentMetadata>
                <div>
                  {new Date(comment.updated_at).toLocaleString()}
                  {comment.updated_at !== comment.created_at && (
                    <span>{t('contract.comments.editedSuffix')}</span>
                  )}
                </div>
              </CommentMetadata>
              {editingComment === comment.id ? (
                <Form>
                  <FormTextArea value={editedComment} onChange={(e) => setEditedComment(e.target.value)} />
                  <Button basic circular icon labelPosition="left" onClick={() => handleEditComment(comment.id)}><Icon name="check" /> {t('contract.comments.actions.save')}</Button>
                  <Button basic circular icon labelPosition="left" onClick={() => setEditingComment(null)}><Icon name="times" /> {t('contract.comments.actions.cancel')}</Button>
                </Form>
              ) : (
                <>
                  <CommentText>
                    {comment.content.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </CommentText>
                  <CommentActions>
                    {userDetails.name === comment.author && (
                      <>
                      <CommentAction onClick={() => { setEditingComment(comment.id); setEditedComment(comment.content); }}><Icon name='edit' title={t('contract.comments.icons.edit')} /></CommentAction>
                      <CommentAction onClick={() => handleDeleteComment(comment.id)}><Icon name='trash' title={t('contract.comments.icons.delete')} /></CommentAction>
                      </>
                    )}
                    <CommentAction onClick={() => { setReplyingTo(comment.id); setShowAddComment(true);}}><Icon name='reply' title={t('contract.comments.icons.reply')} /></CommentAction>
                  </CommentActions>
                  {comment.replies && comment.replies.length > 0 && (
                    <CommentGroup>
                      {comment.replies.map((reply) => (
                        <Comment key={reply.id}>
                          <CommentAvatar src="./resources/icons/user.png" />
                          <CommentContent>
                            <CommentAuthor as="a">{reply.author}</CommentAuthor>
                            <CommentMetadata>
                              <div>
                                {new Date(reply.updated_at).toLocaleString()}
                                {reply.updated_at !== reply.created_at && (
                                  <span>{t('contract.comments.editedSuffix')}</span>
                                )}
                              </div>
                            </CommentMetadata>
                            {editingComment === reply.id ? (
                              <Form>
                                <FormTextArea value={editedComment} onChange={(e) => setEditedComment(e.target.value)} />
                                <Button basic circular icon labelPosition="left" onClick={() => handleEditComment(reply.id)}><Icon name="check" /> {t('contract.comments.actions.save')}</Button>
                                <Button basic circular icon labelPosition="left" onClick={() => setEditingComment(null)}><Icon name="times" /> {t('contract.comments.actions.cancel')}</Button>
                              </Form>
                            ) : (
                              <>
                              <CommentText>
                                {reply.content.split('\n').map((line, index) => (
                                  <React.Fragment key={index}>
                                    {line}
                                    <br />
                                  </React.Fragment>
                                ))}
                              </CommentText>
                              {userDetails.name === reply.author && (
                                <CommentActions>
                                  <CommentAction onClick={() => { setEditingComment(reply.id); setEditedComment(reply.content);}}><Icon name='edit' title={t('contract.comments.icons.edit')} /></CommentAction>
                                  <CommentAction onClick={() => handleDeleteComment(reply.id)}><Icon name='trash' title={t('contract.comments.icons.delete')} /></CommentAction>
                                </CommentActions>
                              )}
                              </>
                            )}
                          </CommentContent>
                        </Comment>
                      ))}
                    </CommentGroup>
                  )}
                </>
              )}
            </CommentContent>
          </Comment>
        ))}
      </CommentGroup>


      {showAddComment ? (
        <>
          <Form style={{marginBottom: '10px'}}>
            <FormTextArea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder={replyingTo ? t('contract.comments.actions.addReply') : t('contract.comments.actions.addNew')}/>
          </Form>
          <Button basic circular icon labelPosition="left" floated="right" onClick={handleAddComment}><Icon name="check" />{t('contract.comments.actions.apply')}</Button>
          <Button basic circular icon labelPosition="left" floated="right" onClick={() => { setShowAddComment(false); setReplyingTo(null); }}><Icon name="times" />{t('contract.comments.actions.cancel')}</Button>
        </>
      ) : (
        <Button basic circular icon labelPosition="left" floated="right" onClick={() => setShowAddComment(true)}><Icon name="add" />{t('contract.comments.actions.addComment')}</Button>
      )}
      <br/>
      <br/>
    </>
  );
};

export default Max2ContractComments;
